import React from "react";

/**
 * Same numbered-page pagination used on the Transactions page (see
 * src/views/pages/Transaction/components/Main.js's renderPagination) - circular page buttons
 * with a purple ring on the active page, "..." for skipped ranges, text arrows on the ends.
 * Reimplemented here rather than imported so this page doesn't take a dependency on Transaction's
 * internal component, but the visual output and behavior are intentionally identical.
 */
const Pagination = ({ pageNumber, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const paginationStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    listStyleType: "none",
    padding: 0,
    marginTop: "20px",
  };

  const dotsStyle = {
    color: "#A0A0A0",
    fontSize: "20px",
    margin: "0 5px",
  };

  const pageButtonStyle = (isActive) => ({
    border: isActive ? "2px solid #7F56D9" : "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    background: "transparent",
    color: "#A0A0A0",
    fontWeight: isActive ? "bold" : "normal",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "16px",
  });

  const pages = [];
  const addDots = (key) => {
    pages.push(
      <li key={key}>
        <span style={dotsStyle}>...</span>
      </li>
    );
  };

  pages.push(
    <li key="prev">
      <button
        onClick={() => onPageChange(pageNumber - 1)}
        disabled={pageNumber === 1}
        style={{
          background: "transparent",
          color: "#A0A0A0",
          border: "none",
          fontSize: "20px",
          cursor: pageNumber === 1 ? "not-allowed" : "pointer",
          opacity: pageNumber === 1 ? 0.5 : 1,
        }}
      >
        &lt;
      </button>
    </li>
  );

  pages.push(
    <li key={1}>
      <button style={pageButtonStyle(pageNumber === 1)} onClick={() => onPageChange(1)} disabled={pageNumber === 1}>
        1
      </button>
    </li>
  );

  if (pageNumber > 3) {
    addDots("start-dots");
  }

  for (let i = Math.max(2, pageNumber - 1); i <= Math.min(totalPages - 1, pageNumber + 1); i++) {
    pages.push(
      <li key={i}>
        <button style={pageButtonStyle(pageNumber === i)} onClick={() => onPageChange(i)}>
          {i}
        </button>
      </li>
    );
  }

  if (pageNumber < totalPages - 2) {
    addDots("end-dots");
  }

  if (totalPages > 1) {
    pages.push(
      <li key={totalPages}>
        <button
          style={pageButtonStyle(pageNumber === totalPages)}
          onClick={() => onPageChange(totalPages)}
          disabled={pageNumber === totalPages}
        >
          {totalPages}
        </button>
      </li>
    );
  }

  pages.push(
    <li key="next">
      <button
        onClick={() => onPageChange(pageNumber + 1)}
        disabled={pageNumber === totalPages}
        style={{
          background: "transparent",
          color: "#A0A0A0",
          border: "none",
          fontSize: "20px",
          cursor: pageNumber === totalPages ? "not-allowed" : "pointer",
          opacity: pageNumber === totalPages ? 0.5 : 1,
        }}
      >
        &gt;
      </button>
    </li>
  );

  return <ul style={paginationStyle}>{pages}</ul>;
};

export default Pagination;
